import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { getProfile, login, setAuthToken } from "../../lib/api";
import type { UserProfile } from "../../types";

interface AuthContextValue {
  user: UserProfile | null;
  token: string | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<UserProfile>;
  applySession: (token: string, user: UserProfile) => void;
  signOut: () => void;
  updateProfilePhoto: (url: string | null) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const TOKEN_KEY = "fitness-coach-token";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(sessionStorage.getItem(TOKEN_KEY));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setAuthToken(null);
      setLoading(false);
      return;
    }

    setAuthToken(token);
    getProfile()
      .then((profile) => setUser(profile))
      .catch(() => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setAuthToken(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      loading,
      async signIn(email: string, password: string) {
        const data = await login(email, password);
        sessionStorage.setItem(TOKEN_KEY, data.access_token);
        setAuthToken(data.access_token);
        setToken(data.access_token);
        setUser(data.user);
        return data.user;
      },
      applySession(nextToken: string, nextUser: UserProfile) {
        sessionStorage.setItem(TOKEN_KEY, nextToken);
        setAuthToken(nextToken);
        setToken(nextToken);
        setUser(nextUser);
      },
      signOut() {
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthToken(null);
        setToken(null);
        setUser(null);
      },
      updateProfilePhoto(url: string | null) {
        setUser((current) => (current ? { ...current, profile_photo_url: url } : current));
      },
    }),
    [loading, token, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
